import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";

import {
  ParsedResponse,
  Rating,
  ResponseMessagesType,
  SessionDataType,
} from "../types/response.types";
import { chatApiResponseParser } from "../../chat-ai/utils/chatApiResponseParser";
import { AnswersMdToHtml } from "../../chat-ai/AnswersMdToHtml";
import { UserMessage } from "../../chat-ai/UserMessage";
import { ChatHistoryPanelItem } from "../../chat-ai/ChatHistoryPanelItem";
import { SessionTimeoutModal } from "../../chat-ai/SessionTimeoutModal";
import { MessageActionsBar } from "../../chat-ai/MessageActionsBar";
import { AgentMessage } from "../../chat-ai/AgentMessage";
import { ChatGreeting } from "../../chat-ai/ChatGreeting";
import { ChatHistoryBar } from "../../chat-ai/ChatHistoryBar";
import { ChatHistoryPanel } from "../../chat-ai/ChatHistoryPanel";
import { ChatInput } from "../../chat-ai/ChatInput";
import { ChatSources } from "../../chat-ai/ChatSources";
import { AgentDisclaimer } from "../../chat-ai/AgentDisclaimer";
import Portal from "../../chat-ai/utils/portal/Portal";
import { RenderIf } from "../../chat-ai/RenderIf";
import { SkeletonLoader } from "../../chat-ai/SkeletonLoader";
import { SuggestedTopics } from "../../chat-ai/SuggestedTopics";
import { Accordion } from "../../accordion/accordion";
import { Drawer } from "../../accordion/drawer";
import Modal from "../../modal";
import { Button } from "../../button/button.jsx";
import { TextInput } from "../../text-input/text-input";
import { Message } from "../../message/message";
import { DrawerHeading } from "../../accordion/drawer.types";
import {
  ButtonSize,
  MessageRole,
  MessageStatus,
  MessageType,
} from "../../types";
import { GenAIChatAgentProps } from "./GenAIChatAgent.types";

const MAX_FAVORITES_LENGTH = 5;
const MAX_RECENT_LENGTH = 15;

export const GenAIChatAgent = ({
  chatData,
  isApiLoading,
  errorOnSubmit,
  isFullScreen = false,
  isSessionTimeoutModalOpen,
  resetState,
  setIsSessionTimeoutModalOpen,
  isChatAiHistoryEnabled = true,
  isChatAiSourceEnabled = true,
  browserSessionTimeout = 30 * 60 * 1000,
  chatSessions = [],
  sessionMessages = [],
  currentChatSessionId,
  setCurrentChatSessionId,
  onSubmit,
  onPutChatFeedback,
  onUpdateChatSessionName,
  onDeleteChatSession,
  onUpdateChatSessionPin,
  refetchChatSessions,
  refetchChatSessionMessages,
  slotProps = {},
}: GenAIChatAgentProps): ReactElement => {
  const [value, setValue] = useState("");
  const [chatAnswers, setChatAnswers] = useState<ParsedResponse>([]);
  const [hasUserAskedAnyQuestion, setHasUserAskedAnyQuestion] = useState(false);

  const [openDeleteChatSessionModal, setOpenDeleteChatSessionModal] =
    useState(false);
  const [openRenameChatSessionModal, setOpenRenameChatSessionModal] =
    useState(false);
  const [chatSessionToDelete, setChatSessionToDelete] =
    useState<SessionDataType>();
  const [chatSessionToRename, setChatSessionToRename] =
    useState<SessionDataType>();
  const [newChatSessionName, setNewChatSessionName] = useState<string>("");
  const [openHistoryPanel, setOpenHistoryPanel] = useState(false);

  const lastMessageRef = useRef<HTMLDivElement>(null);
  const chatContentRef = useRef<HTMLDivElement>(null);
  const openTimestampRef = useRef<number>(Date.now());
  const historyButtonClicked = useRef<boolean>(false);
  const feedbackButtonClicked = useRef<boolean>(false);

  const setCleanChatSession = useCallback((): void => {
    setCurrentChatSessionId(uuidv4());
    setChatAnswers([]);
    setHasUserAskedAnyQuestion(false);
    setValue("");
    if (!isFullScreen) {
      setOpenHistoryPanel(false);
    }
  }, [isFullScreen, setCurrentChatSessionId]);

  useEffect(() => {
    if (chatSessionToRename) {
      setNewChatSessionName(chatSessionToRename.sessionName);
    }
  }, [chatSessionToRename]);

  useEffect(() => {
    if (isSessionTimeoutModalOpen) {
      setCleanChatSession();
    }
  }, [isSessionTimeoutModalOpen, setCleanChatSession]);

  useEffect(() => {
    if (sessionMessages && sessionMessages.length > 0) {
      const sessionMessagesParsed = sessionMessages.map((sessionMessage) => {
        const question = sessionMessage?.request?.question || "";
        const responseMessages: ResponseMessagesType | undefined =
          sessionMessage?.responseMessages;
        const rating = sessionMessage?.rating ?? null;

        return chatApiResponseParser(responseMessages, question, rating);
      });

      setHasUserAskedAnyQuestion(true);
      setChatAnswers([
        ...sessionMessagesParsed
          .flat()
          .filter((item): item is NonNullable<typeof item> => item !== null),
      ]);
    }
  }, [sessionMessages]);

  useEffect(() => {
    if (chatData) {
      setChatAnswers((chatAnswers) => [...chatAnswers, ...chatData]);
    }
  }, [chatData]);

  useEffect(() => {
    if (chatAnswers.length > 0 && feedbackButtonClicked.current === false) {
      refetchChatSessions?.();
      const previousDiv = lastMessageRef.current?.previousElementSibling;
      if (previousDiv) {
        previousDiv.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chatAnswers, refetchChatSessions]);

  useEffect(() => {
    const checkTimeout = (): void => {
      const currentTimestamp = Date.now();
      if (
        currentTimestamp - openTimestampRef.current >=
        browserSessionTimeout
      ) {
        setIsSessionTimeoutModalOpen(true);
      }
    };

    // Check every second
    const intervalId = setInterval(checkTimeout, 1000);

    return (): void => {
      clearInterval(intervalId);
    };
  }, [browserSessionTimeout, setIsSessionTimeoutModalOpen]);

  const favoriteSessions = useMemo(() => {
    return chatSessions?.filter((session) => session.isPinned) || [];
  }, [chatSessions]);

  const recentSessions = useMemo(() => {
    return chatSessions?.filter((session) => !session.isPinned) || [];
  }, [chatSessions]);

  const handleDeleteChatSession = useCallback(
    async (chatSessionId: string): Promise<void> => {
      if (typeof onDeleteChatSession === "function") {
        await onDeleteChatSession(chatSessionId);
        if (chatSessionId === currentChatSessionId) {
          setCleanChatSession();
        }
        refetchChatSessions?.();
      }
    },
    [
      currentChatSessionId,
      onDeleteChatSession,
      refetchChatSessions,
      setCleanChatSession,
    ]
  );

  const handleAddFavoriteChatSession = useCallback(
    async (chatSessionId: string, isPinned: boolean): Promise<void> => {
      if (typeof onUpdateChatSessionPin === "function") {
        await onUpdateChatSessionPin({
          sessionId: chatSessionId,
          isPinned: !isPinned,
        });
        refetchChatSessions?.();
      }
    },
    [onUpdateChatSessionPin, refetchChatSessions]
  );

  const onRenameChatSession = useCallback(
    async (chatSessionId: string, newChatSessionName: string) => {
      if (typeof onUpdateChatSessionName === "function") {
        await onUpdateChatSessionName({
          chatSessionId,
          newChatSessionName,
        });
      }
    },
    [onUpdateChatSessionName]
  );

  const handleChatFeedback = useCallback(
    async (
      sessionId: string,
      responseId: string,
      rating: Rating
    ): Promise<void> => {
      if (typeof onPutChatFeedback === "function") {
        await onPutChatFeedback({ sessionId, responseId, rating });
        feedbackButtonClicked.current = true;
        setTimeout(() => {
          feedbackButtonClicked.current = false;
        }, 500);
        refetchChatSessionMessages?.();
      }
    },
    [onPutChatFeedback, refetchChatSessionMessages]
  );

  const handleSubmit = useCallback(
    async (value?: string): Promise<void> => {
      setHasUserAskedAnyQuestion(true);
      if (value) {
        chatContentRef?.current?.scrollTo({
          top: chatContentRef.current?.scrollHeight,
          behavior: "smooth",
        });
        if (typeof onSubmit === "function") {
          await onSubmit(value);
          // Reset the timestamp
          openTimestampRef.current = Date.now();
        }
      }
    },
    [onSubmit]
  );

  const userHasNotAskedAnyQuestion = !hasUserAskedAnyQuestion;

  const renderChatHistoryPanelButton = useCallback(
    (chatSession: SessionDataType): ReactElement => (
      <ChatHistoryPanelItem
        key={chatSession.sessionId}
        chatSession={chatSession}
        active={chatSession.sessionId === currentChatSessionId}
        isFavoritesFull={favoriteSessions.length === MAX_FAVORITES_LENGTH}
        onChatSessionClick={(chatSessionId: string): void => {
          setCurrentChatSessionId(chatSessionId);
          setValue("");
          historyButtonClicked.current = true;
          setTimeout(() => {
            historyButtonClicked.current = false;
          }, 500);
          if (!isFullScreen) {
            setOpenHistoryPanel(false);
          }
        }}
        onAddFavoriteChatSessionHandler={handleAddFavoriteChatSession}
        onRenameChatSessionHandler={(chatSession): void => {
          setOpenRenameChatSessionModal(true);
          setChatSessionToRename(chatSession);
        }}
        onDeleteChatSessionHandler={(chatSession): void => {
          setOpenDeleteChatSessionModal(true);
          setChatSessionToDelete(chatSession);
        }}
        {...slotProps.chatHistoryPanelItem}
      />
    ),
    [
      currentChatSessionId,
      favoriteSessions.length,
      handleAddFavoriteChatSession,
      slotProps.chatHistoryPanelItem,
      setCurrentChatSessionId,
      isFullScreen,
    ]
  );

  const renderChatConversation = useMemo((): ReactElement[] => {
    return chatAnswers.map((answer, index) => {
      const shouldUseTypingEffect =
        index === chatAnswers.length - 1 &&
        historyButtonClicked.current === false &&
        feedbackButtonClicked.current === false &&
        isApiLoading === false;

      return (
        <div key={answer.responseId}>
          <UserMessage>{answer.query}</UserMessage>
          <AgentMessage>
            <AnswersMdToHtml
              validMarkdownText={answer.chatAnswer.toString()}
              typingSpeed={shouldUseTypingEffect ? 15 : 0}
              {...slotProps.answersMdToHtml}
            />
            <RenderIf condition={!!answer.actionLink && isChatAiSourceEnabled}>
              <ChatSources
                title={answer.title}
                actionLink={answer.actionLink}
              />
            </RenderIf>
            <MessageActionsBar
              sessionId={currentChatSessionId}
              responseId={answer.responseId}
              chatAnswerPlainText={answer.chatAnswerPlainText}
              rating={answer.rating}
              onRatingChangeHandler={handleChatFeedback}
              {...slotProps.messageActionsBar}
            />
          </AgentMessage>
        </div>
      );
    });
  }, [
    chatAnswers,
    currentChatSessionId,
    handleChatFeedback,
    isApiLoading,
    isChatAiSourceEnabled,
    slotProps.answersMdToHtml,
    slotProps.messageActionsBar,
  ]);

  const topicsSmall = [
    { title: "What is Flowtype?" },
    { title: "What are potential full flights?" },
    { title: "How do I change base demand?" },
  ];

  const topics = [
    {
      title: "What is Flowtype?",
      description: "Flow type categorizes how passenger demand...",
    },
    {
      title: "What are potential full flights?",
      description: "Potential full flights are flights expected to be full...",
    },
    {
      title: "How is virtual capacity calculated?",
      description:
        "Virtual Capacity is calculated as Effective capacity + Upgrades...",
    },
    {
      title: "How do I change base demand?",
      description: "As a Demand Analyst, you can adjust base demand and...",
    },
  ];

  return (
    <>
      <div style={{ display: "flex", flex: 1, overflow: "auto" }}>
        <RenderIf condition={isChatAiHistoryEnabled}>
          <ChatHistoryPanel
            isFullScreen={isFullScreen}
            openHistoryPanel={openHistoryPanel}
            {...slotProps.chatHistoryPanel}
          >
            <RenderIf condition={isFullScreen}>
              <ChatHistoryBar
                isFullScreen={true}
                openHistoryPanel={openHistoryPanel}
                disabled={isApiLoading}
                setOpenHistoryPanel={setOpenHistoryPanel}
                handleNewChatClick={(): void => setCleanChatSession()}
                {...slotProps.chatHistoryBar}
              />
            </RenderIf>
            <Accordion>
              <Drawer
                expanded
                title={
                  <>
                    Favorites{" "}
                    <span>
                      ({favoriteSessions.length}/{MAX_FAVORITES_LENGTH})
                    </span>
                  </>
                }
                headingLevel={DrawerHeading.H3}
              >
                {favoriteSessions.length === 0 ? (
                  <span className="spark-pad-l-2">No chats to display</span>
                ) : (
                  favoriteSessions.map((session: SessionDataType) =>
                    renderChatHistoryPanelButton(session)
                  )
                )}
              </Drawer>
              <Drawer
                expanded
                title={
                  <>
                    Recent{" "}
                    <span>
                      ({recentSessions.length}/
                      {MAX_RECENT_LENGTH - favoriteSessions.length})
                    </span>
                  </>
                }
                headingLevel={DrawerHeading.H3}
              >
                {recentSessions.length === 0 ? (
                  <span className="spark-pad-l-2">No chats to display</span>
                ) : (
                  recentSessions.map((session: SessionDataType) =>
                    renderChatHistoryPanelButton(session)
                  )
                )}
              </Drawer>
            </Accordion>

            {openDeleteChatSessionModal && (
              <Portal portalId="chat-delete-modal-portal-root">
                <Modal
                  id="chat-delete-modal"
                  open={openDeleteChatSessionModal}
                  onClose={(): void => {
                    setOpenDeleteChatSessionModal(false);
                    setChatSessionToDelete(undefined);
                  }}
                  title="Delete chat"
                  titleHeadingLevel={2}
                >
                  This will delete{" "}
                  <strong>{chatSessionToDelete?.sessionName}</strong>
                  <nav className="spark-btn-group">
                    <Button
                      className="spark-btn-group-secondary"
                      size={ButtonSize.SMALL}
                      secondary
                      onClick={(): void => {
                        setOpenDeleteChatSessionModal(false);
                        setChatSessionToDelete(undefined);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="spark-btn-group-primary"
                      negative
                      size={ButtonSize.SMALL}
                      onClick={(): void => {
                        setOpenDeleteChatSessionModal(false);
                        setChatSessionToDelete(undefined);
                        handleDeleteChatSession(
                          chatSessionToDelete?.sessionId || ""
                        );
                      }}
                    >
                      Delete
                    </Button>
                  </nav>
                </Modal>
              </Portal>
            )}

            {openRenameChatSessionModal && (
              <Portal portalId="chat-rename-modal-portal-root">
                <Modal
                  id="chat-rename-modal"
                  open={openRenameChatSessionModal}
                  onClose={(): void => setOpenRenameChatSessionModal(false)}
                  title="Rename chat"
                  titleHeadingLevel={2}
                >
                  <TextInput
                    name="chat-rename-input"
                    id="chat-rename-input"
                    label="Rename"
                    maxLength={64}
                    value={newChatSessionName}
                    onChange={(_e, value): void => {
                      setNewChatSessionName(value);
                    }}
                  />

                  <nav className="spark-btn-group">
                    <Button
                      className="spark-btn-group-secondary"
                      size={ButtonSize.SMALL}
                      secondary
                      onClick={(): void => {
                        setOpenRenameChatSessionModal(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="spark-btn-group-primary"
                      size={ButtonSize.SMALL}
                      onClick={(): void => {
                        setOpenRenameChatSessionModal(false);
                        onRenameChatSession(
                          chatSessionToRename?.sessionId || "",
                          newChatSessionName || ""
                        );
                        setChatSessionToRename(undefined);
                        setNewChatSessionName("");
                      }}
                    >
                      Save
                    </Button>
                  </nav>
                </Modal>
              </Portal>
            )}
          </ChatHistoryPanel>
        </RenderIf>
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden",
              width: "100%",
              maxWidth: 700,
            }}
          >
            <RenderIf condition={isChatAiHistoryEnabled && !isFullScreen}>
              <ChatHistoryBar
                isFullScreen={false}
                openHistoryPanel={openHistoryPanel}
                disabled={isApiLoading}
                setOpenHistoryPanel={setOpenHistoryPanel}
                handleNewChatClick={(): void => setCleanChatSession()}
              />
            </RenderIf>
            <div
              style={{
                overflow: "auto",
                padding: "16px 24px 0 24px",
                flex: 1,
                width: "100%",
                ...(userHasNotAskedAnyQuestion
                  ? {
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }
                  : {}),
              }}
              ref={chatContentRef}
            >
              <RenderIf condition={userHasNotAskedAnyQuestion}>
                <>
                  <ChatGreeting
                    isFullScreen={isFullScreen}
                    data-testid="chat-greeting"
                    {...slotProps.chatGreeting}
                  />
                  <SuggestedTopics
                    className="spark-mar-b-2"
                    isApiLoading={isApiLoading}
                    setValue={setValue}
                    clickToSubmit={handleSubmit}
                    topics={topics}
                    {...slotProps.suggestedTopics}
                  />
                </>
              </RenderIf>
              <section>
                {renderChatConversation}
                <div ref={lastMessageRef} />
              </section>
            </div>
            <div
              style={{
                padding: "16px 24px 16px 24px",
                width: "100%",
              }}
            >
              <RenderIf condition={hasUserAskedAnyQuestion}>
                <SuggestedTopics
                  isApiLoading={isApiLoading}
                  setValue={setValue}
                  clickToSubmit={handleSubmit}
                  topics={topicsSmall}
                  {...slotProps.suggestedTopics}
                />
              </RenderIf>

              <RenderIf condition={isApiLoading}>
                <SkeletonLoader />
              </RenderIf>

              <section>
                <RenderIf condition={!!errorOnSubmit}>
                  <Message
                    className="spark-mar-t-1"
                    content={errorOnSubmit ?? ""}
                    status={MessageStatus.ERROR}
                    type={MessageType.PAGE}
                    role={MessageRole.ALERT}
                  />
                </RenderIf>

                <ChatInput
                  value={value}
                  onChange={(event): void => setValue(event.target.value)}
                  onSubmit={handleSubmit}
                  disabledSubmitButton={isApiLoading}
                  userHasNotAskedAnyQuestion={userHasNotAskedAnyQuestion}
                  {...slotProps.chatInput}
                />

                <AgentDisclaimer
                  isFullScreen={isFullScreen}
                  modalContent=""
                  {...slotProps.agentDisclaimer}
                />
              </section>
            </div>
          </div>
        </div>
      </div>

      <SessionTimeoutModal
        isSessionTimeoutModalOpen={isSessionTimeoutModalOpen}
        resetState={resetState}
        isFullScreen={isFullScreen}
        setIsSessionTimeoutModalOpen={setIsSessionTimeoutModalOpen}
        {...slotProps.sessionTimeoutModal}
      />
    </>
  );
};
